// ---------------------------------------------------------------------------
// Comfort: how hard the replay is allowed to hit the person watching it
// (ethical audit E06). Separate from graphics quality on purpose — a fast
// machine is no reason to shake someone's screen, and a slow one is no reason
// to spare them — and chosen before the first replay, not discovered in it.
// It also sets how much the console itself moves (command/motion): its
// monitors switching over, static, and the chart's interference.
//
// Defaults follow the operating system's reduced-motion setting. Kept in
// local storage; a browser that will not store it gets the defaults.
// ---------------------------------------------------------------------------

export type FlashLevel = 'full' | 'reduced' | 'off';
/** Full: the terminal's motion. Reduced: short fades only. Off: every change is instant. */
export type MotionLevel = 'full' | 'reduced' | 'off';

export interface Comfort {
  /** The camera jolts on heavy impacts. */
  shake: boolean;
  /** Screen flashes on heavy impacts and the whiteout of a device. */
  flashes: FlashLevel;
  /** Watch each exchange, or go straight to its written report. */
  replays: 'play' | 'report';
  /** How the console moves between phases and views. */
  motion: MotionLevel;
}

const KEY = 'tb.comfort';

function prefersReducedMotion(): boolean {
  try { return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false; } catch { return false; }
}

/**
 * Automated browsers (the UI scripts, the visual checks) get an instant
 * console unless they store a choice, so a screenshot never lands mid-change.
 */
function automated(): boolean {
  try { return navigator.webdriver === true; } catch { return false; }
}

export function defaultComfort(reducedMotion = prefersReducedMotion(), scripted = automated()): Comfort {
  const motion: MotionLevel = scripted ? 'off' : reducedMotion ? 'reduced' : 'full';
  return reducedMotion
    ? { shake: false, flashes: 'reduced', replays: 'play', motion }
    : { shake: true, flashes: 'full', replays: 'play', motion };
}

/** Whatever was stored, mended field by field against the defaults. */
export function readComfort(stored: string | null, base = defaultComfort()): Comfort {
  if (!stored) return base;
  try {
    const v = JSON.parse(stored) as Partial<Comfort>;
    return {
      shake: typeof v.shake === 'boolean' ? v.shake : base.shake,
      flashes: v.flashes === 'full' || v.flashes === 'reduced' || v.flashes === 'off' ? v.flashes : base.flashes,
      replays: v.replays === 'report' || v.replays === 'play' ? v.replays : base.replays,
      motion: v.motion === 'full' || v.motion === 'reduced' || v.motion === 'off' ? v.motion : base.motion,
    };
  } catch {
    return base;
  }
}

export function comfort(): Comfort {
  let stored: string | null = null;
  try { stored = localStorage.getItem(KEY); } catch { /* defaults */ }
  return readComfort(stored);
}

const listeners = new Set<(c: Comfort) => void>();

export function storeComfort(c: Comfort): void {
  try { localStorage.setItem(KEY, JSON.stringify(c)); } catch { /* this session only */ }
  for (const fn of listeners) fn(c);
}

/** Told whenever a choice is stored, so an open console follows it at once. */
export function onComfort(fn: (c: Comfort) => void): () => void {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}
